import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Box,
  Paper,
  Divider,
  CircularProgress,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { toast } from 'react-toastify';

// Define field mapping information
const exportFields = {
  name: {
    name: 'User Name',
    description: 'Full name of the user',
    defaultSelected: true,
  },
  username: {
    name: 'Username',
    description: 'Username for login',
    defaultSelected: true,
  },
  email: {
    name: 'Email',
    description: 'User email address',
    defaultSelected: true,
  },
  role: {
    name: 'Role',
    description: 'User role in the system',
    defaultSelected: true,
  },
  group: {
    name: 'Group Name',
    description: 'TL/Organization name',
    defaultSelected: true,
  },
  isAdmin: {
    name: 'Admin Status',
    description: 'Whether user has admin privileges',
    defaultSelected: true,
  },
  isStudent: {
    name: 'Student Status',
    description: 'Whether user is a student',
    defaultSelected: true,
  },
  position: {
    name: 'Position',
    description: 'User position code',
    defaultSelected: false,
  },
  other_position: {
    name: 'Other Position',
    description: 'Custom position description',
    defaultSelected: false,
  },
  department: {
    name: 'Department',
    description: 'Academic department',
    defaultSelected: false,
  },
  degree: {
    name: 'Degree',
    description: 'Academic degree',
    defaultSelected: false,
  },
  graduation_year: {
    name: 'Graduation Year',
    description: 'Expected graduation year',
    defaultSelected: false,
  },
  current_year: {
    name: 'Current Year',
    description: 'Current academic year',
    defaultSelected: false,
  },
  phone_number: {
    name: 'Phone Number',
    description: 'Contact phone number',
    defaultSelected: false,
  },
  created: {
    name: 'Account Created',
    description: 'Account creation date',
    defaultSelected: false,
  },
};

// Mapping for position values
const positionLabels = {
  0: 'TL student management body',
  1: 'Manager (other technical clubs)',
  2: 'TL Manager',
  3: 'TL Technician',
  4: 'Other',
  5: 'Professor In Charge',
};

const UserExport = ({ users, currentUserCollegeName }) => {
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [selectedFields, setSelectedFields] = useState(
    Object.fromEntries(
      Object.entries(exportFields).map(([key, field]) => [
        key,
        field.defaultSelected,
      ])
    )
  );
  const [isExporting, setIsExporting] = useState(false);

  // Handler for opening the export dialog
  const handleOpenExportDialog = () => {
    setIsExportDialogOpen(true);
  };

  // Handler for closing the export dialog
  const handleCloseExportDialog = () => {
    setIsExportDialogOpen(false);
  };

  // Toggle selection of a field
  const handleFieldToggle = field => {
    setSelectedFields(prev => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  // Select all fields
  const handleSelectAll = () => {
    setSelectedFields(
      Object.fromEntries(Object.keys(exportFields).map(key => [key, true]))
    );
  };

  // Deselect all fields
  const handleDeselectAll = () => {
    setSelectedFields(
      Object.fromEntries(Object.keys(exportFields).map(key => [key, false]))
    );
  };

  // Convert datetime string to readable format
  const formatDate = dateString => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };
  // Format role display
  const formatRole = user => {
    if (!user.isAdmin && user.isStudent) {
      return 'Student';
    } else {
      const basePositionLabel = positionLabels[user.position] || 'N/A';
      if (user.position === '4' && user.other_position) {
        return `${basePositionLabel} (${user.other_position})`;
      } else {
        return basePositionLabel;
      }
    }
  };

  // Format user data for export
  const formatUserDataForExport = user => {
    const formattedData = {};

    if (selectedFields.name) formattedData['User Name'] = user.name || '';
    if (selectedFields.username) formattedData.Username = user.username || '';
    if (selectedFields.email) formattedData.Email = user.email || '';
    if (selectedFields.role) formattedData.Role = formatRole(user);
    if (selectedFields.group) formattedData['Group Name'] = user.group || '';
    if (selectedFields.isAdmin)
      formattedData['Admin Status'] = user.isAdmin ? 'Yes' : 'No';
    if (selectedFields.isStudent)
      formattedData['Student Status'] = user.isStudent ? 'Yes' : 'No';
    if (selectedFields.position)
      formattedData.Position = positionLabels[user.position] || '';
    if (selectedFields.other_position)
      formattedData['Other Position'] = user.other_position || '';
    if (selectedFields.department)
      formattedData.Department = user.department || '';
    if (selectedFields.degree) formattedData.Degree = user.degree || '';
    if (selectedFields.graduation_year)
      formattedData['Graduation Year'] = user.graduation_year || '';
    if (selectedFields.current_year)
      formattedData['Current Year'] = user.current_year || '';
    if (selectedFields.phone_number)
      formattedData['Phone Number'] = user.phone_number || '';
    if (selectedFields.created)
      formattedData['Account Created'] = formatDate(user.created);

    return formattedData;
  };

  // Handle the export process
  const handleExport = () => {
    if (Object.values(selectedFields).every(val => !val)) {
      toast.error('Please select at least one field to export');
      return;
    }

    setIsExporting(true);

    try {
      // Format all users
      const formattedUsers = users.map(formatUserDataForExport);

      if (formattedUsers.length === 0) {
        toast.warning('No users to export');
        setIsExporting(false);
        return;
      }

      // Get headers from the first user
      const headers = Object.keys(formattedUsers[0]);

      // Create CSV content
      let csvContent = headers.join(',') + '\n';

      // Add data rows
      formattedUsers.forEach(user => {
        const row = headers.map(header => {
          // Handle fields that might contain commas or quotes
          const cell = String(user[header] || '');
          if (cell.includes(',') || cell.includes('"') || cell.includes('\n')) {
            return `"${cell.replace(/"/g, '""')}"`;
          }
          return cell;
        });
        csvContent += row.join(',') + '\n';
      });

      // Create and download the file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute(
        'download',
        `${currentUserCollegeName}_users_export_${new Date().toISOString().split('T')[0]}.csv`
      );
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setIsExporting(false);
      setIsExportDialogOpen(false);
      toast.success('Users exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export users');
      setIsExporting(false);
    }
  };

  // Count selected fields
  const selectedFieldCount =
    Object.values(selectedFields).filter(Boolean).length;

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        onClick={handleOpenExportDialog}
        startIcon={<FileDownloadIcon />}
        sx={{
          borderRadius: '8px',
          textTransform: 'none',
          fontWeight: 500,
          '&:hover': {
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          },
        }}
      >
        Export to CSV
      </Button>

      <Dialog
        open={isExportDialogOpen}
        onClose={handleCloseExportDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: { borderRadius: '10px' },
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #eee', pb: 1 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6" fontWeight={500}>
              Export Users to CSV
            </Typography>
            <Chip
              label={`${selectedFieldCount} fields selected`}
              color="primary"
              size="small"
            />
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body1" gutterBottom>
            Select the fields you want to include in the export:
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, mb: 2, mt: 1 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={handleSelectAll}
              sx={{ textTransform: 'none' }}
            >
              Select All
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={handleDeselectAll}
              sx={{ textTransform: 'none' }}
            >
              Deselect All
            </Button>
          </Box>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: '8px' }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              User Fields
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 2,
              }}
            >
              {Object.entries(exportFields).map(([key, field]) => (
                <FormGroup key={key}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedFields[key] || false}
                        onChange={() => handleFieldToggle(key)}
                        color="primary"
                        size="small"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2">{field.name}</Typography>
                        <Tooltip title={field.description} arrow>
                          <IconButton size="small">
                            <HelpOutlineIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    }
                  />
                </FormGroup>
              ))}
            </Box>
          </Paper>

          <Box sx={{ mt: 3, display: 'flex', alignItems: 'center' }}>
            <CheckCircleIcon fontSize="small" color="success" sx={{ mr: 1 }} />
            <Typography variant="body2" color="text.secondary">
              {users.length} user{users.length !== 1 ? 's' : ''} will be
              exported
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ borderTop: '1px solid #eee', p: 2 }}>
          <Button
            onClick={handleCloseExportDialog}
            color="inherit"
            disabled={isExporting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleExport}
            variant="contained"
            color="primary"
            disabled={isExporting || selectedFieldCount === 0}
            startIcon={
              isExporting ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <FileDownloadIcon />
              )
            }
          >
            {isExporting ? 'Exporting...' : 'Export CSV'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UserExport;
